/**
 * Build-time injected configuration.
 *
 * These `{{...}}` tokens are replaced at build time by KatalWebpackPlugin
 * (see configuration/webpack.config.js -> replacements). At runtime they hold
 * the concrete CloudFormation values. If a replacement is missing or fails,
 * the literal token (or an empty string) survives into the bundle -- which is
 * exactly the "served HTTP 200 but pointing at the wrong/no endpoint" failure
 * mode that current server-side monitoring cannot see. validateInjectedConfig
 * lets the app emit a metric when that happens.
 */
export const InjectedConfig = {
    region: '{{Region}}',
    cloudFrontProtocol: '{{CloudFrontProtocol}}',
    cloudFrontDomain: '{{CloudFrontDomain}}',
    // Sushi metrics domain for this stage: 'prod' (prod cells) or 'test'
    // (alpha/beta). Drives whether metrics reach prod PMET or Andes-only.
    domain: '{{Domain}}',
} as const;

export interface ConfigValidationResult {
    valid: boolean;
    /** Config keys that were missing or malformed. */
    invalidKeys: string[];
}

/** A value is unresolved if it is empty or still carries a template token. */
const isUnresolved = (value: string): boolean =>
    value == null || value.length === 0 || value.includes('{{') || value.includes('}}');

const REGION_PATTERN = /^[a-z]{2}-[a-z]+-\d$/;
const PROTOCOL_PATTERN = /^https?$/;
const DOMAIN_PATTERN = /^[a-zA-Z0-9.-]+$/;
// Sushi metrics domain -- 'prod' routes to prod PMET, 'master'/'test' to
// pre-prod (Andes). Matches the FITUICDK Domain enum (prod/master/test).
export const METRICS_DOMAIN_VALUES = ['prod', 'master', 'test'];

/**
 * Validate the build-time injected config. Returns the list of keys that are
 * missing or malformed so the caller can emit a per-key metric.
 */
export const validateInjectedConfig = (
    config: {region: string; cloudFrontProtocol: string; cloudFrontDomain: string; domain: string} = InjectedConfig,
): ConfigValidationResult => {
    const invalidKeys: string[] = [];

    if (isUnresolved(config.region) || !REGION_PATTERN.test(config.region)) {
        invalidKeys.push('Region');
    }
    if (isUnresolved(config.cloudFrontProtocol) || !PROTOCOL_PATTERN.test(config.cloudFrontProtocol)) {
        invalidKeys.push('CloudFrontProtocol');
    }
    if (isUnresolved(config.cloudFrontDomain) || !DOMAIN_PATTERN.test(config.cloudFrontDomain)) {
        invalidKeys.push('CloudFrontDomain');
    }
    if (isUnresolved(config.domain) || !METRICS_DOMAIN_VALUES.includes(config.domain)) {
        invalidKeys.push('Domain');
    }

    return {valid: invalidKeys.length === 0, invalidKeys};
};
